// src/pages/FundPlanner/components/Funds/ImportStructureModal.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ModalBackdrop from "components/ModalBackdrop";
import { supabase } from "services/supabaseClient";
import {
  getImportPreviewRows,
  isImportableStructure,
} from "./structureImport";

function fmtDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function getSettlementDate(structure) {
  return String(structure?.form?.settlementDate ?? "").trim();
}

export default function ImportStructureModal({
  isOpen,
  onClose,
  onImport,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [structures, setStructures] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const loadStructures = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");

    const { data, error } = await supabase
      .from("fp_funds")
      .select("id, org_id, tenant_id, name, fund_type, updated_at, form, solution")
      .not("solution", "is", null)
      .order("updated_at", { ascending: false });

    if (error) {
      setStructures([]);
      setLoadError(error.message || "Unable to load structures.");
      setIsLoading(false);
      return;
    }

    const rows = (Array.isArray(data) ? data : []).filter(
      (row) =>
        isImportableStructure(row) &&
        Array.isArray(row?.solution?.investments) &&
        row.solution.investments.length > 0
    );

    setStructures(rows);
    setSelectedId(rows[0]?.id || null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadStructures();
  }, [isOpen, loadStructures]);

  const selectedStructure = useMemo(
    () => structures.find((row) => row.id === selectedId) || null,
    [structures, selectedId]
  );

  const previewRows = useMemo(() => {
    if (!selectedStructure) return [];
    return getImportPreviewRows(selectedStructure);
  }, [selectedStructure]);

  const handleImport = useCallback(async () => {
    if (!selectedStructure || isImporting) return;
    setIsImporting(true);
    try {
      await onImport?.(selectedStructure);
    } finally {
      setIsImporting(false);
    }
  }, [isImporting, onImport, selectedStructure]);

  if (!isOpen) return null;

  return (
    <ModalBackdrop>
      <div
        className="bg-app text-app w-full max-w-6xl rounded-2xl shadow-xl border border-[hsl(var(--border))] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-structure-title"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--border))]">
          <div>
            <h2 id="import-structure-title" className="text-lg font-semibold text-app">
              Import Structure
            </h2>
            <p className="text-sm text-muted mt-1">
              Select a saved Structurer solution from{" "}
              <span className="font-mono-app">fp_funds</span>.
            </p>
          </div>

          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="grid grid-cols-12 min-h-[540px]">
          <div className="col-span-4 border-r border-[hsl(var(--border))]">
            <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
              <div className="text-sm font-medium text-app">Available Structures</div>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={loadStructures}
                disabled={isLoading}
              >
                Refresh
              </button>
            </div>

            <div className="max-h-[480px] overflow-auto">
              {isLoading ? (
                <div className="p-4 text-sm text-muted">Loading structures...</div>
              ) : loadError ? (
                <div className="p-4 text-sm text-muted">{loadError}</div>
              ) : structures.length === 0 ? (
                <div className="p-4 text-sm text-muted">
                  No importable structures were found.
                </div>
              ) : (
                structures.map((row) => {
                  const preview = getImportPreviewRows(row);
                  const isSelected = row.id === selectedId;

                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => setSelectedId(row.id)}
                      className={`w-full text-left px-4 py-3 border-b border-[hsl(var(--border))]
                        ${
                          isSelected
                            ? "bg-[hsl(var(--accent))]"
                            : "bg-app hover:bg-[hsl(var(--accent))]/50"
                        }`}
                    >
                      <div className="font-medium text-app">
                        {row.name || "(Untitled Structure)"}
                      </div>
                      <div className="text-xs text-muted mt-1">
                        Type: {row.fund_type || "—"}
                      </div>
                      <div className="text-xs text-muted">
                        Settlement: {getSettlementDate(row) || "—"}
                      </div>
                      <div className="text-xs text-muted">
                        Importable positions: {preview.length}
                      </div>
                      <div className="text-xs text-muted">
                        Updated: {fmtDateTime(row.updated_at) || "—"}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="col-span-8 flex flex-col">
            <div className="px-4 py-3 border-b border-[hsl(var(--border))]">
              <div className="text-sm font-medium text-app">Preview</div>
            </div>

            {!selectedStructure ? (
              <div className="p-4 text-sm text-muted">
                Select a structure to preview the positions that will be imported.
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-app">
                  <div className="text-sm font-medium text-app">
                    {selectedStructure.name || "(Untitled Structure)"}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {previewRows.length} position{previewRows.length === 1 ? "" : "s"} will be imported
                  </div>
                </div>

                <div className="overflow-auto max-h-[420px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-app border-b border-[hsl(var(--border))]">
                      <tr>
                        <th className="text-left px-4 py-2 text-app font-medium">Type</th>
                        <th className="text-left px-4 py-2 text-app font-medium">CUSIP / ID</th>
                        <th className="text-left px-4 py-2 text-app font-medium">Description</th>
                        <th className="text-right px-4 py-2 text-app font-medium">Amount</th>
                        <th className="text-right px-4 py-2 text-app font-medium">Price</th>
                        <th className="text-left px-4 py-2 text-app font-medium">Maturity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row) => (
                        <tr
                          key={row.id}
                          className="border-b border-[hsl(var(--border))]"
                        >
                          <td className="px-4 py-2 text-app">{row.investment_type || ""}</td>
                          <td className="px-4 py-2 font-mono-app text-app">
                            {row.cusip_or_id || ""}
                          </td>
                          <td className="px-4 py-2 text-app">
                            {row.raw_security_description || ""}
                          </td>
                          <td className="px-4 py-2 text-right font-mono-app text-app">
                            {row.par_amount != null && row.par_amount !== ""
                              ? Number(row.par_amount).toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })
                              : ""}
                          </td>
                          <td className="px-4 py-2 text-right font-mono-app text-app">
                            {row.purchase_price != null && row.purchase_price !== ""
                              ? Number(row.purchase_price).toLocaleString(undefined, {
                                  minimumFractionDigits: 3,
                                  maximumFractionDigits: 3,
                                })
                              : ""}
                          </td>
                          <td className="px-4 py-2 font-mono-app text-app">
                            {row.raw_maturity_date || ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="mt-auto px-5 py-4 border-t border-[hsl(var(--border))] flex justify-end gap-2">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleImport}
                disabled={!selectedStructure || isImporting}
              >
                {isImporting ? "Importing..." : "Import Structure"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}
